import React, { useState, useEffect, useMemo } from 'reactn'
import './cartSidebar.css'
import { ExpressCheckoutElement, useElements, useStripe, Elements } from '@stripe/react-stripe-js'
import { paymentFailed } from '../../state/analytics'
import { API } from 'aws-amplify'
import { Box } from '@chakra-ui/react'

// Appearance for express checkout Elements
const appearance = {
  theme: 'none',
  rules: {
    '.Input': {
      border: '1px solid #000',
      borderRadius: '0px',
      boxShadow: 'none',
      fontSize: '14px',
      letterSpacing: 'normal',
      padding: '10px',
      outline: 'none',
      marginBottom: '0px',
      textTransform: 'uppercase',
    },
  },
  variables: {
    colorPrimary: '#000',
    colorBackground: '#fff',
    fontWeight: '300',
    borderRadius: '0px',
    fontSize: '14px',
    colorText: '#30313d',
    colorDanger: '#df1b41',
  }
}

// Inner component that uses the Elements context
const ExpressCheckoutInner = ({ globalState, disabled = false, setProcessing = false, expressCartData, setExpressCartData }) => {
  const { store } = globalState
  const { pd, ts, soe } = store

  const stripe = useStripe()
  const elements = useElements()
  // Always use express cart price - never use main cart price for express checkout
  const [currentTotal, setCurrentTotal] = useState(expressCartData?.ts?.total_price_pence || 0)
  const [linkAvailable, setLinkAvailable] = useState(false)
  // Store the last address used for onShippingRateChange
  const [lastAddress, setLastAddress] = useState(null)

  // Update total when express cart data changes
  useEffect(() => {
    if (expressCartData?.ts?.total_price_pence) {
      setCurrentTotal(expressCartData.ts.total_price_pence);
    }
  }, [expressCartData?.ts?.total_price_pence]);

  const expressCheckoutOptions = {
    buttonType: {
      googlePay: 'checkout',
      applePay: 'check-out',
      paypal: 'checkout'
    },
    buttonTheme: {
      applePay: 'black',
      googlePay: 'black',
      paypal: 'black'
    },
    emailRequired: true,
    phoneNumberRequired: true,
    shippingAddressRequired: true,
    billingAddressRequired: true,
    business: { name: 'INGMARSON' },
  }

  const onReady = ({ availablePaymentMethods }) => {
    // Check if Link is available as a payment method
    // availablePaymentMethods might be undefined, null, or not an array
    const hasLink = Array.isArray(availablePaymentMethods) && availablePaymentMethods.some(method => method === 'link')
    setLinkAvailable(hasLink || false)
    console.log('[EXPRESS READY DEBUG] Available payment methods:', availablePaymentMethods, 'Link available:', hasLink)
  };

  const onShippingRateChange = async ({ resolve, shippingRate }) => {
    try {
      // If we have a shipping rate selected, fetch cart with that shipping option
      if (shippingRate?.id && lastAddress) {
        const items = store.cartProducts ? store.cartProducts.map(item => ({
          ownGarment: item.own_garment,
          kvatt: item.kvatt,
          sku: item.sku,
          quantity: item.quantity,
        })) : [];

        const emailToUse = lastAddress.email || (linkAvailable ? store.emailAddress : null);

        const cartData = {
          ss: store.ss,
          ec: true,
          ca: {
            items: items,
            promo: store.promoCode?.promo_code || "",
            sa: {
              country_code: lastAddress.country,
              zip: lastAddress.postal_code,
              address1: lastAddress.line1 || '',
              address2: lastAddress.line2 || '',
              city: lastAddress.city,
              locality: lastAddress.state,
              name: lastAddress.name || '',
              phone: lastAddress.phone || '',
              complete: true
            },
            sp: {
              shippingID: shippingRate.id, // Use the selected shipping rate ID
              packagingID: store.packagingID
            },
            ...(emailToUse ? { cd: { email: emailToUse } } : {})
          },
        };

        const response = await API.post('api', '/checkout/cart', {
          body: cartData
        });

        if (response?.ss) {
          setExpressCartData(response);
          const newTotal = response?.ts?.total_price_pence;

          console.log('[EXPRESS SHIPPING RATE CHANGE] New price:', {
            shippingRateId: shippingRate.id,
            newTotal: newTotal,
            newTotalGBP: newTotal ? (newTotal / 100).toFixed(2) : 'none'
          });

          if (newTotal) {
            setCurrentTotal(newTotal);
            console.log('[EXPRESS SHIPPING RATE CHANGE] Resolving with total:', newTotal, `(£${(newTotal / 100).toFixed(2)})`);

            // Resolve with new total
            resolve({
              total: {
                amount: newTotal,
                currency: 'gbp'
              }
            });

            // Refresh Elements to reflect updated payment intent amount from backend
            if (elements) {
              try {
                await elements.fetchUpdates();
                console.log('[EXPRESS] Elements refreshed after shipping rate change');
              } catch (fetchError) {
                console.warn('[EXPRESS] Could not fetch updates from Elements:', fetchError);
              }
            }

            return;
          }
        }
      }

      // Fallback: resolve with current total
      resolve({
        total: {
          amount: currentTotal || expressCartData?.ts?.total_price_pence || 0,
          currency: 'gbp'
        }
      });
    } catch (error) {
      console.error('[EXPRESS] Error in onShippingRateChange:', error);
      resolve({
        total: {
          amount: currentTotal || expressCartData?.ts?.total_price_pence || 0,
          currency: 'gbp'
        }
      });
    }
  };

  const onShippingAddressChange = async ({ resolve, address }) => {
    try {
      // Store the address for use in onShippingRateChange
      setLastAddress(address);

      const items = store.cartProducts ? store.cartProducts.map(item => ({
        ownGarment: item.own_garment,
        kvatt: item.kvatt,
        sku: item.sku,
        quantity: item.quantity,
      })) : [];

      // Use email from express checkout address if provided
      // If Link is available and address.email is not provided, use Link email as fallback
      // Do NOT use Link email for other express methods (Klarna/Apple Pay/etc) - they don't have email yet
      const emailToUse = address.email || (linkAvailable ? store.emailAddress : null)
      console.log('[EXPRESS EMAIL DEBUG] onShippingAddressChange:', {
        addressEmail: address.email,
        linkAvailable: linkAvailable,
        storeEmailAddress: store.emailAddress,
        emailToUse: emailToUse,
        tsEmail: ts?.email,
        note: linkAvailable && !address.email ? 'Using Link email as fallback' : 'Using address.email or no email'
      })

      const cartData = {
        ss: store.ss,
        ec: true,
        ca: {
          items: items,
          promo: store.promoCode?.promo_code || "",
          sa: {
            country_code: address.country,
            zip: address.postal_code,
            address1: address.line1 || '',
            address2: address.line2 || '',
            city: address.city,
            locality: address.state,
            name: address.name || '',
            phone: address.phone || '',
            complete: true
          },
          sp: {
            shippingID: store.shippingID,
            packagingID: store.packagingID
          },
          // Include email if:
          // 1. Provided by express checkout (address.email) - for all methods
          // 2. Link is available and address.email is not provided - use Link email as fallback
          // Do NOT use Link email for other express methods (Klarna/Apple Pay/etc) - they don't have email yet
          ...(emailToUse ? { cd: { email: emailToUse } } : {})
        },
      };

      const response = await API.post('api', '/checkout/cart', {
        body: cartData
      });

      if (response?.ss) {
        setExpressCartData(response);
        console.log('[EXPRESS CART] Payment intent data received:', {
          hasExpressPd: !!response?.expressPd?.cs,
          hasPd: !!response?.pd?.cs,
          expressPdCs: response?.expressPd?.cs ? `${response.expressPd.cs.substring(0, 20)}...` : 'none',
          pdCs: response?.pd?.cs ? `${response.pd.cs.substring(0, 20)}...` : 'none',
          expressPi: response?.expressPd?.pi || 'none',
          pi: response?.pd?.pi || 'none',
          expressTotalPrice: response?.ts?.total_price,
          expressTotalPricePence: response?.ts?.total_price_pence,
          mainCartTotalPrice: ts?.total_price,
          mainCartTotalPricePence: ts?.total_price_pence
        });
      }

      const shippingOptions = response?.soe || store.soe || [];
      const formattedShippingRates = shippingOptions.map(option => ({
          id: option.id,
          displayName: option.displayName,
          detail: `${option.deliveryEstimate.minimum.value}-${option.deliveryEstimate.maximum.value} days`,
          amount: option.amount
        }));

      const totalAmount = response?.ts?.total_price_pence;

      console.log('[EXPRESS PRICE UPDATE] Resolving with new price:', {
        totalAmount: totalAmount,
        totalAmountGBP: totalAmount ? (totalAmount / 100).toFixed(2) : 'none',
        responseTotalPrice: response?.ts?.total_price,
        responseTotalPricePence: response?.ts?.total_price_pence,
        currentTotal: currentTotal,
        expressCartDataTotal: expressCartData?.ts?.total_price_pence
      });

      if (totalAmount) {
        setCurrentTotal(totalAmount);

        // Resolve immediately with the new total from express cart response
        // Stripe Express Checkout will update the displayed price from the resolved total
        // Ensure amount is a number (not string) and matches payment intent amount
        const resolvedTotal = {
            shippingRates: formattedShippingRates,
            total: {
            amount: Number(totalAmount), // Ensure it's a number
            currency: 'gbp'
          }
        };

        // Check if payment intent amount matches the resolved total
        // Stripe Express Checkout uses payment intent amount for display, not resolved total
        // After backend updates payment intent, we need to refresh Elements to show new amount
        // IMPORTANT: Make sure we're using the EXPRESS Elements, not the main cart Elements
        if (response?.expressPd?.pi && stripe && elements) {
          try {
            const expressPaymentIntentId = response.expressPd.pi;
            const { paymentIntent } = await stripe.retrievePaymentIntent(response.expressPd.cs);

            // Verify we're using the express payment intent
            console.log('[EXPRESS ELEMENTS CHECK] Verifying Elements context:', {
              expressPaymentIntentId: expressPaymentIntentId,
              mainCartPaymentIntentId: pd?.pi,
              retrievedPaymentIntentId: paymentIntent?.id,
              retrievedAmount: paymentIntent?.amount,
              retrievedAmountGBP: paymentIntent?.amount ? `£${(paymentIntent.amount / 100).toFixed(2)}` : 'none',
              resolvedTotal: totalAmount,
              resolvedTotalGBP: `£${(totalAmount / 100).toFixed(2)}`,
              isExpressPaymentIntent: paymentIntent?.id === expressPaymentIntentId,
              isMainCartPaymentIntent: paymentIntent?.id === pd?.pi
            });

            // CRITICAL: Only proceed if this is the express payment intent, not the main cart one
            if (paymentIntent?.id !== expressPaymentIntentId) {
              console.error('[EXPRESS ERROR] Using wrong payment intent!', {
                expected: expressPaymentIntentId,
                got: paymentIntent?.id,
                note: 'Elements context is wrong - using main cart Elements instead of express Elements'
              });
              // Don't return - still need to resolve so Stripe can continue
              // Just log the error and continue
            }

            if (paymentIntent && paymentIntent.amount !== totalAmount) {
              console.error('[EXPRESS PRICE MISMATCH] Payment intent amount does not match resolved total!', {
                paymentIntentId: response.expressPd.pi,
                paymentIntentAmount: paymentIntent.amount,
                paymentIntentAmountGBP: `£${(paymentIntent.amount / 100).toFixed(2)}`,
                resolvedTotal: totalAmount,
                resolvedTotalGBP: `£${(totalAmount / 100).toFixed(2)}`,
                note: 'Backend must update payment intent amount to match cart total'
              });
            } else {
              console.log('[EXPRESS PRICE MATCH] Payment intent amount matches resolved total:', {
                paymentIntentId: response.expressPd.pi,
              amount: totalAmount,
                gbp: `£${(totalAmount / 100).toFixed(2)}`
              });

              // Refresh Elements to reflect updated payment intent amount
              // This ensures the UI displays the correct price after backend updates it
              // Get the ExpressCheckoutElement specifically to ensure we're updating the right element
              try {
                const expressCheckoutElement = elements.getElement('expressCheckout');
                if (expressCheckoutElement) {
                  console.log('[EXPRESS] Found ExpressCheckoutElement, fetching updates...');
                  await elements.fetchUpdates();
                  console.log('[EXPRESS] Elements refreshed to show updated payment intent amount');
                } else {
                  console.warn('[EXPRESS] ExpressCheckoutElement not found in elements context');
                  // Try fetchUpdates anyway in case it works without the element
                  await elements.fetchUpdates();
                }
              } catch (fetchError) {
                console.warn('[EXPRESS] Could not fetch updates from Elements:', fetchError);
              }
            }
          } catch (error) {
            console.warn('[EXPRESS] Could not retrieve payment intent to verify amount:', error);
          }
        }

        console.log('[EXPRESS PRICE UPDATE] Resolving with total:', {
          totalAmount: totalAmount,
          totalAmountType: typeof totalAmount,
          resolvedAmount: resolvedTotal.total.amount,
          resolvedAmountType: typeof resolvedTotal.total.amount,
          gbp: `£${(totalAmount / 100).toFixed(2)}`,
          paymentIntentId: response?.expressPd?.pi
        });

        resolve(resolvedTotal);
      } else {
        // Always use express cart price if available, never fallback to main cart
        const expressTotal = expressCartData?.ts?.total_price_pence || currentTotal;
        console.warn('[EXPRESS PRICE UPDATE] No totalAmount in response, using fallback:', expressTotal);
        resolve({
          shippingRates: formattedShippingRates,
          total: {
            amount: expressTotal,
            currency: 'gbp'
          }
        });
      }
    } catch (error) {
      console.error('[EXPRESS] Error in onShippingAddressChange:', error);
      // Always use express cart price - never use main cart price for express checkout
      const fallbackTotal = expressCartData?.ts?.total_price_pence || currentTotal || 0;
      resolve({
        shippingRates: (store.soe || []).map(option => ({
          id: option.id,
          displayName: option.displayName,
          detail: `${option.deliveryEstimate.minimum.value}-${option.deliveryEstimate.maximum.value} days`,
          amount: option.amount
        })),
        total: {
          amount: fallbackTotal,
          currency: 'gbp'
        }
      });
    }
  };

  const onCancel = () => {
    // Express payment is separate - don't update global state
    // Just clear express cart data
    setExpressCartData(null);
  };

  const handleCheckout = async (event) => {
    setProcessing(true);

    if (!stripe || !elements) {
      paymentFailed(store, { type: 'validation_error', message: 'Payment system not ready. Please try again.' });
      setProcessing(false);
      return;
    }

    // Use express payment intent (expressPd.cs) if available, otherwise fallback to pd.cs
    // Express checkout should have its own separate payment intent
    const paymentIntentClientSecret = expressCartData?.expressPd?.cs || expressCartData?.pd?.cs;

    console.log('[EXPRESS CHECKOUT] Using payment intent:', {
      hasExpressPd: !!expressCartData?.expressPd?.cs,
      hasPd: !!expressCartData?.pd?.cs,
      usingExpressPd: !!expressCartData?.expressPd?.cs,
      expressPi: expressCartData?.expressPd?.pi || 'none',
      pi: expressCartData?.pd?.pi || 'none',
      clientSecret: paymentIntentClientSecret ? `${paymentIntentClientSecret.substring(0, 20)}...` : 'none'
    });

    if (!paymentIntentClientSecret) {
      paymentFailed(store, { type: 'validation_error', message: 'Payment details missing. Please try again.' });
      setProcessing(false);
      return;
    }

    const returnURL = process.env.GATSBY_RETURN_URL;

    // Only use email from express checkout - don't fallback to main cart email
    // If no email from express checkout, omit receipt_email and let Stripe use what it collected
    const receiptEmail = expressCartData?.ts?.email;

    const confirmParams = {
      return_url: returnURL
    };

    // // Only include receipt_email if we have it from express checkout
    // if (receiptEmail) {
    //   confirmParams.receipt_email = receiptEmail;
    // }

    const result = await stripe.confirmPayment({
      elements,
      clientSecret: paymentIntentClientSecret,
      confirmParams: confirmParams,
    });

    // // Handle 3D Secure redirect (Stripe usually handles this automatically, but check just in case)
    // if (result?.paymentIntent?.status === 'requires_action' &&
    //     result?.paymentIntent?.next_action?.type === 'redirect_to_url') {
    //   console.log('[EXPRESS PAYMENT] 3D Secure redirect required:', result.paymentIntent.next_action.redirect_to_url.url);
    //   // Stripe should handle redirect automatically, but if not, redirect manually
    //   if (result.paymentIntent.next_action.redirect_to_url?.url) {
    //     window.location.href = result.paymentIntent.next_action.redirect_to_url.url;
    //     navigate(result.paymentIntent.next_action.redirect_to_url.url)
    //     return; // Don't continue - redirect is happening
    //   }
    // }

    if (result?.error) {
      paymentFailed(store, { type: result.error.type, message: result.error.message, code: result.error.code });
      setProcessing(false);
    } else {
      // Payment confirmed successfully (or redirect will happen automatically)
      setProcessing(false);
    }
  }

  return (
    <>
      <ExpressCheckoutElement
        options={expressCheckoutOptions}
        onReady={onReady}
        onShippingAddressChange={onShippingAddressChange}
        onShippingRateChange={onShippingRateChange}
        onCancel={onCancel}
        onConfirm={handleCheckout}
      />
    </>
  )
}

// Outer component that provides Elements context with express payment intent
const ExpressPayment = ({ globalState, disabled = false, setProcessing = false }) => {
  const { store, updateGlobal } = globalState
  const { pd, stripe, expressCartData: globalExpressCartData } = store
  // Use global state express cart data if available, otherwise use local state
  const [localExpressCartData, setLocalExpressCartData] = useState(null);
  const expressCartData = globalExpressCartData || localExpressCartData;

  // Helper to set express cart data in both local and global state
  const setExpressCartData = (data) => {
    setLocalExpressCartData(data);
    if (data) {
      updateGlobal({ expressCartData: data });
    }
  };

  // Memoize express stripe options - ONLY use expressPd.cs, never fallback to main cart pd.cs
  // The price must come from express cart data
  const expressStripeOptions = useMemo(() => {
    // Only use express payment intent - never use main cart payment intent for express checkout
    const expressClientSecret = expressCartData?.expressPd?.cs;
    if (!expressClientSecret || !stripe) return null;

    console.log('[EXPRESS ELEMENTS] Using express payment intent:', {
      hasExpressPd: !!expressCartData?.expressPd?.cs,
      expressPi: expressCartData?.expressPd?.pi || 'none',
      clientSecret: expressClientSecret ? `${expressClientSecret.substring(0, 20)}...` : 'none',
      expressTotalPrice: expressCartData?.ts?.total_price,
      expressTotalPricePence: expressCartData?.ts?.total_price_pence
    });

    return {
      clientSecret: expressClientSecret,
      appearance: appearance
    };
  }, [expressCartData?.expressPd?.cs, stripe, expressCartData?.ts?.total_price_pence]);

  // Fetch express cart on mount or when checkout opens - fetch when we have shipping address
  // BUT: If we already have express payment intent from main cart, skip the fetch
  // The main cart fetch includes express payment intent, so we can use it immediately
  useEffect(() => {
    const fetchInitialExpressCart = async () => {
      // If we already have express payment intent from main cart, don't fetch
      // It will be refreshed anyway when needed
      if (expressCartData?.expressPd?.cs) {
        console.log('[EXPRESS CART] Using express payment intent from main cart, skipping separate fetch');
        return;
      }

      // Fetch if:
      // 1. We don't have express cart data yet
      // 2. We have cart products
      // 3. We have shipping address (backend handles defaults)
      const hasShippingAddress = store?.shipping_address?.country_code;
      const hasCartProducts = store?.cartProducts?.length > 0;
      const hasSS = store?.ss?.oid;

      if (!expressCartData && hasCartProducts && hasSS && hasShippingAddress) {
        try {
          const items = store.cartProducts.map(item => ({
            ownGarment: item.own_garment,
            kvatt: item.kvatt,
            sku: item.sku,
            quantity: item.quantity,
          }));

          const cartData = {
            ss: store.ss,
            ec: true,
            ca: {
              items: items,
              promo: store.promoCode?.promo_code || "",
              sa: {
                country_code: store.shipping_address.country_code,
                zip: store.shipping_address.zip,
                address1: store.shipping_address.address1 || '',
                address2: store.shipping_address.address2 || '',
                city: store.shipping_address.city,
                locality: store.shipping_address.locality,
                name: store.fullName || '',
                phone: store.contactNumber || '',
                complete: true
              },
              sp: {
                shippingID: store.shippingID,
                packagingID: store.packagingID
              },
              ...(store.emailAddress ? { cd: { email: store.emailAddress } } : {})
            },
          };

          const response = await API.post('api', '/checkout/cart', {
            body: cartData
          });

          if (response?.ss) {
            setExpressCartData(response);
            console.log('[EXPRESS CART] Initial fetch on mount:', {
              hasExpressPd: !!response?.expressPd?.cs,
              expressTotalPrice: response?.ts?.total_price,
              expressTotalPricePence: response?.ts?.total_price_pence
            });
          }
        } catch (error) {
          console.error('[EXPRESS] Error fetching initial express cart:', error);
        }
      }
    };

    fetchInitialExpressCart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store?.checkoutOpen, store?.cartProducts?.length, store?.ss?.oid]); // Fetch when checkout opens or cart changes

  // Memoize fonts array
  const stripeFonts = useMemo(() => {
    return [
      {family: 'ApercuLight', src: 'url(https://ingmarson.com/fonts/apercu-light.woff)', weight: '300'}
    ];
  }, []);

  // Determine if express payment intent is ready
  const isExpressReady = !!expressStripeOptions && !!stripe;
  const isDisabled = disabled || !isExpressReady;

  // Always render the container, but show disabled state until ready
  if (!stripe) {
    return null;
  }

  // If we have express payment intent, render with Elements
  if (expressStripeOptions) {
    return (
      <Elements
        fonts={stripeFonts}
        options={expressStripeOptions}
        stripe={stripe}
        loader="auto"
      >
        <ExpressCheckoutInner
          globalState={globalState}
          disabled={isDisabled}
          setProcessing={setProcessing}
          expressCartData={expressCartData}
          setExpressCartData={setExpressCartData}
        />
      </Elements>
    );
  }

  // If no express payment intent yet, show minimal placeholder
  // This allows the UI space to be reserved while payment intent loads
  // The placeholder is very subtle and will be replaced quickly
  return (
    <Box
      width="100%"
      opacity={0.3}
      pointerEvents="none"
      minHeight="40px"
      display="flex"
      alignItems="center"
      justifyContent="center"
    >
      {/* Minimal placeholder - will be replaced by real Elements when ready */}
    </Box>
  );
}

export default ExpressPayment
